import { t } from "@lingui/macro";
import { useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  UserCheckIcon,
  SpinnerIcon,
} from "@phosphor-icons/react";
import { useReferences } from "@/client/services/reference/references";
import { useFilterReference } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { useExportReference } from "@/client/services/reference/export-reference";
import qs from "qs";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const ReferencesAdminPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "opened" | "closed">("all");

  const { filter, resetFilter, replaceFilter } = useFilterReference();
  const { references, error, loading } = useReferences(page, filter);
  const { refetch } = useExportReference(qs.stringify(filter));
  const { open } = useDialog("filter-reference");

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filter };
    delete nextFilter.upUserId;
    delete nextFilter.downUserId;
    delete nextFilter.status;

    if (search.trim()) {
      nextFilter.upUserId = search.trim();
    }
    if (status !== "all") {
      nextFilter.status = status;
    }

    setPage(1);
    replaceFilter(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setPage(1);
    resetFilter();
  };

  // Colonnes basées sur vos données sans en inventer
  const columns: Column<any>[] = [
    {
      key: "id",
      header: "ID",
      render: (item) => <span className="font-medium text-admin-text-primary">{item.id}</span>,
    },
    {
      key: "upUser",
      header: "Parrain",
      render: (item) => (
        <span className="text-admin-text-secondary">
          {item.upUser?.name || item.upUser?.email || "—"}
        </span>
      ),
    },
    {
      key: "downUser",
      header: "Filleul",
      render: (item) => (
        <span className="text-admin-text-secondary">
          {item.downUser?.name || item.downUser?.email || "—"}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Date de création",
      render: (item) => (
        <span className="text-admin-text-secondary">
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (item) => (
        <Badge variant={item.status === "opened" ? "success" : "warning"}>
          {item.status === "opened" ? "Ouvert" : item.status === "closed" ? "Fermé" : "Inconnu"}
        </Badge>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Parrainnage</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Parrainage`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher des parrainages…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Références affichées"
              number={references?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Page actuelle"
              number={page.toString()}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <UserCheckIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">● Live</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`mis à jour`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Pages totales"
              number={references?.page?.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="État"
              number={loading ? "Chargement" : "Prêt"}
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <SpinnerIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-admin-warning text-xs">● Actif</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`en direct`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher par identifiant, parrain ou filleul…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres Selects */}
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Statut</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 min-w-32 bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder={t`Tous les statuts`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`Tous les statuts`}</SelectItem>
                  <SelectItem value="opened">Ouvert</SelectItem>
                  <SelectItem value="closed">Fermé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <Button
                variant="outline"
                onClick={applyQuickFilters}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => open("create", { id: "filter-reference" })}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Advanced filters
              </Button>
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="h-10 text-admin-text-muted hover:text-admin-text-primary"
              >
                Reset
              </Button>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
              >
                <ExportIcon size={16} className="mr-2" /> Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={references?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination (Style UserAdminPage) */}
      {references?.page !== 0 && (
        <div className="my-3 flex flex-row justify-end space-x-3">
          {page > 1 && (
            <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
              <CaretCircleLeftIcon size={32} weight="regular" />
            </Button>
          )}
          {page - 2 > 0 && (
            <Button
              variant="outline"
              onClick={() => handleSpecficpage(page - 2)}
              className="rounded-lg"
            >
              {page - 2}
            </Button>
          )}
          {page - 1 > 0 && (
            <Button
              variant="outline"
              onClick={() => handleSpecficpage(page - 1)}
              className="rounded-lg"
            >
              {page - 1}
            </Button>
          )}

          <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
            {page}
          </Button>
          {references && page + 1 <= references.page && (
            <Button
              variant="outline"
              onClick={() => handleSpecficpage(page + 1)}
              className="rounded-lg"
            >
              {page + 1}
            </Button>
          )}
          {references && page + 2 <= references.page && (
            <Button
              variant="outline"
              onClick={() => handleSpecficpage(page + 2)}
              className="rounded-lg"
            >
              {page + 2}
            </Button>
          )}
          {references && page < references.page && (
            <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
              <CaretCircleRightIcon size={32} weight="regular" />
            </Button>
          )}
        </div>
      )}
    </>
  );
};
